import { addDays, eachDayOfInterval, format, parseISO, startOfMonth, subDays } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import type { TimeCardShift } from '../../../../../js/api';
import { ShiftRateTypesEnum, ShiftTypesEnum } from '../../../../../js/api';
import { BaseCalendar } from '../../../components/base-calendar';
import { Button } from '../../../components/button';
import { Checkbox } from '../../../components/checkbox';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../../components/dialog';
import { Field, Label } from '../../../components/fieldset';
import { Input } from '../../../components/input';
import { useCodeDefinitionsAsOptions } from '../../../hooks/useCodeDefinitions';
import { getShiftTypeButtonColor } from '../../../utils/globalStyles';
import { DEFAULT_SHIFT_SETTINGS, getShiftTypeDefaults } from '../../../utils/shiftTimes';
import { SHIFT_TYPE_ICONS, SHIFT_TYPE_LABELS, reactSelectReset } from '../../../utils/utils';

type FacilityOption = {
  value: string;
  label: string;
  shift_length?: number;
  shift_start_day?: string;
  shift_start_night?: string;
  shift_start_24h?: string;
  /** Rate types the facility actually bills for via this connection.
   *  Drives the shift-type button visibility + the On Call checkbox. */
  shift_rate_types?: ShiftRateTypesEnum[];
};

const ALL_SHIFT_TYPES = [ShiftTypesEnum.DAY, ShiftTypesEnum.NIGHT, ShiftTypesEnum._24H];

// Maps a `ShiftRateTypesEnum` value to the `ShiftTypesEnum` it implies on the
// picker. Note: CALL is rendered as the "On Call" checkbox, not a top-level
// shift-type button — so it's intentionally absent here.
const RATE_TYPE_TO_SHIFT_TYPE: Record<string, ShiftTypesEnum | undefined> = {
  [ShiftRateTypesEnum.DAY]: ShiftTypesEnum.DAY,
  [ShiftRateTypesEnum.NIGHT]: ShiftTypesEnum.NIGHT,
  [ShiftRateTypesEnum._24H]: ShiftTypesEnum._24H,
};

interface ShiftEditModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (shiftId: string, data: Record<string, any>) => void;
  onCreate?: (data: Record<string, any>) => void;
  mode: 'edit' | 'add';
  shift?: TimeCardShift;
  facilityOptions: FacilityOption[];
  isPending?: boolean;
  /** Used to open the calendar to the correct month in add mode */
  timecardStartOn?: string;
  /** Used to compute allowed date range (7 days before start to 7 days after end) */
  timecardEndOn?: string;
  /** Dates that already have shifts (not selectable in add mode) */
  existingShiftDates?: string[];
  /** Default facility to pre-select in add mode */
  defaultFacilityId?: string;
}

const DATE_FORMAT_DISPLAY = 'EEE, MMM d, yyyy';

/* eslint-disable sonarjs/cognitive-complexity */
export const ShiftFormModal: React.FC<ShiftEditModalProps> = ({
  open,
  onClose,
  onSave,
  onCreate,
  mode,
  shift,
  facilityOptions,
  isPending = false,
  timecardStartOn,
  timecardEndOn,
  existingShiftDates = [],
  defaultFacilityId,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(() => startOfMonth(new Date()));
  const [startTime, setStartTime] = useState('07:00');
  const [endTime, setEndTime] = useState('19:00');
  const [facilityId, setFacilityId] = useState('');
  const [shiftType, setShiftType] = useState<ShiftTypesEnum | null>(null);
  const [onCall, setOnCall] = useState(false);
  const [bonusAmount, setBonusAmount] = useState<number>(0);
  const [holidayAmount, setHolidayAmount] = useState<number>(0);
  const [explanation, setExplanation] = useState('');
  const [removalReason, setRemovalReason] = useState('');

  const { data: removalReasonOptions } = useCodeDefinitionsAsOptions('RemovalReason');
  const activeRemovalReasonOptions = useMemo(
    () => removalReasonOptions.filter((o) => o.is_active),
    [removalReasonOptions]
  );
  const isRemoved = mode === 'edit' && shift?.is_removed;

  // The selected facility's connection determines which shift-rate types
  // are valid. We surface only those buttons (and the On Call checkbox).
  // Fallback strategy:
  //   - selectedFacilityOption undefined (no facility selected yet) → ALL,
  //     so the picker isn't empty before a facility is chosen.
  //   - shift_rate_types undefined (option came from a source that didn't
  //     populate it — defensive) → ALL.
  //   - shift_rate_types is [] (connection legitimately has zero rates
  //     configured) → NONE, matching the assignment-create UX.
  const selectedFacilityOption = useMemo(
    () => facilityOptions.find((f) => f.value === facilityId),
    [facilityOptions, facilityId]
  );
  const availableShiftTypes = useMemo<ShiftTypesEnum[]>(() => {
    if (!selectedFacilityOption) return ALL_SHIFT_TYPES;
    const allowedRates = selectedFacilityOption.shift_rate_types;
    if (!allowedRates) return ALL_SHIFT_TYPES;
    return ALL_SHIFT_TYPES.filter((t) => {
      const rate = Object.entries(RATE_TYPE_TO_SHIFT_TYPE).find(([, st]) => st === t)?.[0];
      return rate ? allowedRates.includes(rate as ShiftRateTypesEnum) : false;
    });
  }, [selectedFacilityOption]);
  const onCallAllowed = useMemo(() => {
    if (!selectedFacilityOption) return true;
    const allowedRates = selectedFacilityOption.shift_rate_types;
    if (!allowedRates) return true;
    return allowedRates.includes(ShiftRateTypesEnum.CALL);
  }, [selectedFacilityOption]);

  // When the selected facility changes, drop any picks that aren't valid
  // for the new connection so a stale rate type can't be submitted.
  useEffect(() => {
    if (shiftType && !availableShiftTypes.includes(shiftType)) {
      setShiftType(null);
    }
    if (onCall && !onCallAllowed) {
      setOnCall(false);
    }
  }, [availableShiftTypes, onCallAllowed, shiftType, onCall]);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && shift) {
      setStartTime(shift.start_time?.slice(0, 5) || '07:00');
      setEndTime(shift.end_time?.slice(0, 5) || '19:00');
      setFacilityId(shift.facility?.id || '');
      setShiftType((shift.shift_type as ShiftTypesEnum) || ShiftTypesEnum.DAY);
      setOnCall(shift.shift_rate_type === ShiftRateTypesEnum.CALL);
      setBonusAmount(shift.bonus_amount ?? 0);
      setHolidayAmount(shift.holiday_amount ?? 0);
      setExplanation(shift.explanation || '');
      setRemovalReason(shift.removal_reason || '');
      setSelectedDate(null);
    } else if (mode === 'add') {
      setShiftType(null);
      setOnCall(false);
      setStartTime('07:00');
      setEndTime('19:00');
      setFacilityId(defaultFacilityId || (facilityOptions[0]?.value ?? ''));
      setBonusAmount(0);
      setHolidayAmount(0);
      setExplanation('');
      setSelectedDate(null);
      setCurrentMonth(
        timecardStartOn ? startOfMonth(parseISO(timecardStartOn)) : startOfMonth(new Date())
      );
    }
  }, [open, mode, shift, timecardStartOn, defaultFacilityId, facilityOptions]);

  // Reset start/end times from the selected facility's shift config whenever
  // the user picks a different shift type or facility. Always overwrites —
  // matches the assignment edit modal's behavior.
  const applyShiftTypeDefaults = useCallback(
    (type: ShiftTypesEnum, forFacilityId: string) => {
      const facility = facilityOptions.find((f) => f.value === forFacilityId);
      const { startTime: nextStart, endTime: nextEnd } = getShiftTypeDefaults(type, {
        shift_length: facility?.shift_length || DEFAULT_SHIFT_SETTINGS.shift_length,
        shift_start_day: facility?.shift_start_day || DEFAULT_SHIFT_SETTINGS.shift_start_day,
        shift_start_night: facility?.shift_start_night || DEFAULT_SHIFT_SETTINGS.shift_start_night,
        shift_start_24h: facility?.shift_start_24h || DEFAULT_SHIFT_SETTINGS.shift_start_24h,
      });
      setStartTime(nextStart);
      setEndTime(nextEnd);
    },
    [facilityOptions]
  );

  const handleShiftTypeChange = (type: ShiftTypesEnum) => {
    setShiftType(type);
    if (facilityId) applyShiftTypeDefaults(type, facilityId);
  };

  const handleFacilityChange = (id: string) => {
    setFacilityId(id);
    if (shiftType) applyShiftTypeDefaults(shiftType, id);
  };

  // Compute allowed date range for add mode: 7 days before first shift to 7 days after last shift
  const calendarFromDate = useMemo(() => {
    if (!timecardStartOn) return undefined;
    return subDays(parseISO(timecardStartOn), 7);
  }, [timecardStartOn]);

  const allowedDates = useMemo(() => {
    if (mode !== 'add' || !timecardStartOn || !timecardEndOn) return [];
    const rangeStart = subDays(parseISO(timecardStartOn), 7);
    const rangeEnd = addDays(parseISO(timecardEndOn), 7);
    const allDates = eachDayOfInterval({ start: rangeStart, end: rangeEnd });
    const existingSet = new Set(existingShiftDates);
    return allDates
      .filter((d) => !existingSet.has(format(d, 'yyyy-MM-dd')))
      .map((d) => format(d, 'yyyy-MM-dd'));
  }, [mode, timecardStartOn, timecardEndOn, existingShiftDates]);

  const disabledDates = useMemo(() => {
    if (mode !== 'add') return [];
    return existingShiftDates.map((d) => parseISO(d));
  }, [mode, existingShiftDates]);

  // Compute start_on and end_on
  const startOn = useMemo(() => {
    if (mode === 'edit') return shift?.start_on || '';
    return selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '';
  }, [mode, shift, selectedDate]);

  const endOn = useMemo(() => {
    if (!startOn) return '';
    if (shiftType === ShiftTypesEnum.NIGHT || shiftType === ShiftTypesEnum._24H) {
      return format(addDays(parseISO(startOn), 1), 'yyyy-MM-dd');
    }
    return startOn;
  }, [startOn, shiftType]);

  // Validation
  const isValid = useMemo(() => {
    if (!shiftType) return false;
    if (!startTime || !endTime) return false;
    if (!facilityId) return false;
    if (mode === 'add' && !selectedDate) return false;
    return !(startOn === endOn && endTime <= startTime);
  }, [shiftType, startTime, endTime, facilityId, mode, selectedDate, startOn, endOn]);

  /* eslint-disable sonarjs/cognitive-complexity */
  const handleSubmit = () => {
    const shiftRateType = onCall
      ? ShiftRateTypesEnum.CALL
      : (shiftType as unknown as ShiftRateTypesEnum);

    if (mode === 'edit' && shift) {
      const data: Record<string, any> = {};
      if (startTime !== shift.start_time?.slice(0, 5)) data.start_time = startTime + ':00';
      if (endTime !== shift.end_time?.slice(0, 5)) data.end_time = endTime + ':00';
      if (endOn !== shift.end_on) data.end_on = endOn;
      if (facilityId !== shift.facility?.id) data.facility = facilityId;
      if (shiftType !== shift.shift_type) data.shift_type = shiftType;
      if (shiftRateType !== shift.shift_rate_type) data.shift_rate_type = shiftRateType;
      if (bonusAmount !== (shift.bonus_amount ?? 0)) data.bonus_amount = bonusAmount;
      if (holidayAmount !== (shift.holiday_amount ?? 0)) data.holiday_amount = holidayAmount;
      if (explanation !== (shift.explanation || '')) data.explanation = explanation;
      if (removalReason !== (shift.removal_reason || '')) data.removal_reason = removalReason;
      onSave(shift.id, data);
    } else if (mode === 'add' && onCreate) {
      onCreate({
        start_on: startOn,
        start_time: startTime + ':00',
        end_on: endOn,
        end_time: endTime + ':00',
        facility: facilityId,
        shift_type: shiftType,
        shift_rate_type: shiftRateType,
        bonus_amount: bonusAmount,
        holiday_amount: holidayAmount,
      });
    }
  };

  const dateSuffix =
    mode === 'edit' && startOn ? ` — ${format(parseISO(startOn), DATE_FORMAT_DISPLAY)}` : '';
  const titleText = mode === 'edit' ? `Edit Shift${dateSuffix}` : 'Add Shift';

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle className="tw:!text-base tw:!font-semibold tw:!mb-1 tw:p-2 tw:rounded-lg tw:bg-brand-green-0 tw:!text-white">
        {titleText}
      </DialogTitle>
      <DialogBody className="tw:space-y-4">
        {/* Dates — read-only for edit, calendar for add */}
        {mode === 'edit' && startOn && (
          <div className="tw:grid tw:grid-cols-2 tw:gap-4">
            <Field>
              <Label>Start Date</Label>
              <div className="tw:text-sm tw:font-medium tw:text-zinc-900">
                {format(parseISO(startOn), DATE_FORMAT_DISPLAY)}
              </div>
            </Field>
            <Field>
              <Label>End Date</Label>
              <div className="tw:text-sm tw:font-medium tw:text-zinc-900">
                {endOn ? format(parseISO(endOn), DATE_FORMAT_DISPLAY) : '--'}
              </div>
            </Field>
          </div>
        )}
        {mode === 'add' && (
          <Field>
            <Label>Select Date</Label>
            <BaseCalendar
              availableDates={allowedDates}
              selectedDates={selectedDate ? [selectedDate] : []}
              onSelectedDatesChange={(dates) => setSelectedDate(dates[dates.length - 1] || null)}
              numberOfMonths={1}
              currentMonth={currentMonth}
              onMonthChange={setCurrentMonth}
              disabledDates={disabledDates}
              fromDate={calendarFromDate}
              className="tw:mx-auto rdp-compact"
            />
          </Field>
        )}

        {/* Shift Type Buttons — shown after date selected in add mode */}
        {(mode === 'edit' || selectedDate) && (
          <Field>
            <Label>Shift Type</Label>
            <div className="tw:flex tw:gap-2 tw:flex-wrap">
              {availableShiftTypes.map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`tw:px-3 tw:py-1.5 tw:!rounded-lg tw:border tw:text-sm tw:font-medium tw:cursor-pointer tw:transition-colors ${getShiftTypeButtonColor(type, shiftType === type)}`}
                  onClick={() => handleShiftTypeChange(type)}
                >
                  <i className={`fas ${SHIFT_TYPE_ICONS[type]} tw:mr-1`} />
                  {SHIFT_TYPE_LABELS[type]}
                </button>
              ))}
            </div>
            {onCallAllowed && (
              <div className="tw:flex tw:items-center tw:gap-2 tw:mt-2">
                <Checkbox checked={onCall} onChange={(checked) => setOnCall(checked)} />
                <span className="tw:text-sm tw:text-zinc-600">On Call</span>
              </div>
            )}
          </Field>
        )}

        {/* Time Fields */}
        {shiftType && (
          <div className="tw:grid tw:grid-cols-2 tw:gap-4">
            <Field>
              <Label>Start Time</Label>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </Field>
            <Field>
              <Label>End Time</Label>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </Field>
          </div>
        )}

        {/* Facility */}
        {shiftType && facilityOptions.length > 0 && (
          <Field>
            <Label>Facility</Label>
            <Select
              options={facilityOptions}
              value={facilityOptions.find((f) => f.value === facilityId) || null}
              onChange={(opt) => handleFacilityChange(opt?.value || '')}
              placeholder="Select facility..."
              styles={reactSelectReset}
              isClearable={false}
            />
          </Field>
        )}

        {/* Bonus & Holiday */}
        {shiftType && (
          <div className="tw:grid tw:grid-cols-2 tw:gap-4">
            <Field>
              <Label>Bonus ($)</Label>
              <Input
                type="number"
                min={0}
                value={bonusAmount || ''}
                onChange={(e) => setBonusAmount(parseInt(e.target.value, 10) || 0)}
                placeholder="0"
              />
            </Field>
            <Field>
              <Label>Holiday ($)</Label>
              <Input
                type="number"
                min={0}
                value={holidayAmount || ''}
                onChange={(e) => setHolidayAmount(parseInt(e.target.value, 10) || 0)}
                placeholder="0"
              />
            </Field>
          </div>
        )}

        {/* Removal Reason — only for removed shifts */}
        {isRemoved && (
          <Field>
            <Label>Removal Reason</Label>
            <Select
              options={activeRemovalReasonOptions}
              value={activeRemovalReasonOptions.find((o) => o.value === removalReason) || null}
              onChange={(opt) => setRemovalReason(opt?.value || '')}
              placeholder="Select reason..."
              styles={reactSelectReset}
              isClearable={false}
            />
          </Field>
        )}

        {/* Explanation — timecard-specific */}
        {shiftType && (
          <Field>
            <Label>Explanation</Label>
            <textarea
              className="tw:w-full tw:rounded-lg tw:bg-white tw:border tw:border-zinc-300 tw:px-3 tw:py-2 tw:text-sm tw:placeholder-zinc-400 tw:focus:border-brand-green-1 tw:focus:ring-1 tw:focus:ring-brand-green-1 tw:outline-none"
              rows={2}
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              placeholder="Optional"
            />
          </Field>
        )}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button color="brandgreen0" onClick={handleSubmit} disabled={!isValid || isPending}>
          {isPending ? 'Saving...' : mode === 'add' ? 'Add Shift' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
